import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import TextInput from "common/blocks/TextInput";
import UIIcon from "common/blocks/UIIcon";
import useFinanceDataContext from "features/vehicleDetails/providers/financeData/useFinanceDataContext";
import usePartExchangeQuestionsContext from "features/vehicleDetails/providers/partExchangeQuestions/usePartExchangeQuestionsContext";
import useKeysValidation from "hooks/useKeysValidation/useKeysValidation";
import { DIGITS_REGEX } from "lib/constants";
import { useForm } from "react-hook-form";
import { scrollIntoView } from "seamless-scroll-polyfill";
import formatCurrency from "utils/formatCurrency";
import generateGtmFinanceSubmitEvent from "utils/generateGtmFinanceSubmitEvent";

import styles from "./FinanceDealWidget.module.scss";
import { financeDealWidgetSchema } from "./schema";

const cx = classNames.bind(styles);

export type FinanceDealWidgetProps = {
    strongText: string;
    text: string;
    buttonText: string;
    textInputPlaceholder: string;
    setDefaultDepositOnLoad?: boolean;
    stockBookNumber: number;
    legacyVehicleId: number;
    minDepositAmount: number;
    maxDepositAmount: number;
    vehicleAmount: number;
};

const FinanceDealWidget = ({
    strongText,
    text,
    buttonText,
    textInputPlaceholder,
    setDefaultDepositOnLoad = false,
    stockBookNumber,
    legacyVehicleId,
    minDepositAmount,
    maxDepositAmount,
    vehicleAmount,
}: FinanceDealWidgetProps) => {
    const [hasValidDeposit, setHasValidDeposit] = useState(false);
    const {
        financeData,
        isHpShowing,
        getFinanceData,
        setDepositFromFinanceWidget,
        milageSelection: mileage,
    } = useFinanceDataContext();

    const { partExQuestionsAnswersResponse: valuation } = usePartExchangeQuestionsContext();
    const { handleKeyDownEvent, handlePasteEvent } = useKeysValidation();

    const partExchangeAmount = valuation?.Valuation ?? 0;
    const hasValuation = !!partExchangeAmount;

    const defaultDeposit = financeData?.find((data) => data.Label === "Deposit")?.Value ?? 0;
    const defaultMonthly = financeData?.find((data) => data.Label === "Monthly Payment")?.Value;
    const defaultTerm = financeData?.find((data) => data.Label === "Duration")?.Value;

    const { register, watch, handleSubmit, setValue, formState, setError, clearErrors } = useForm<{
        deposit: string | undefined;
    }>({
        reValidateMode: "onSubmit",
        defaultValues: {
            deposit: undefined,
        },
        resolver: zodResolver(financeDealWidgetSchema(minDepositAmount, maxDepositAmount)),
    });

    const handleChangeDepositClick = () => {
        setHasValidDeposit(false);
        setValue("deposit", undefined);
    };

    const handleSeeFinanceCalculatorClick = () => {
        const fc = document.getElementById("finance-calculator");

        if (fc) {
            scrollIntoView(fc, { block: "center", behavior: "smooth" });
        }
    };

    // If setDefaultDepositOnLoad is true then we use the
    // deposit data from finance deal to populate
    useEffect(() => {
        if (financeData && setDefaultDepositOnLoad) {
            setValue("deposit", defaultDeposit?.toString() ?? "0");
        }
    }, [defaultDeposit, financeData, setDefaultDepositOnLoad, setValue]);

    const onSubmitFailure = (deposit: number | undefined, error?: string) => {
        setHasValidDeposit(false);
        setValue("deposit", deposit?.toString());
        setDepositFromFinanceWidget(deposit);
        if (error) setError("deposit", { message: error });
    };

    const onSubmitSuccess = (deposit: number) => {
        clearErrors("deposit");
        setDepositFromFinanceWidget(deposit);
        setHasValidDeposit(true);
    };

    const onSubmit = async (data: { deposit: string | undefined }) => {
        const { deposit } = data;
        const genericError =
            "It looks like we’ve had trouble generating a quote for the details you shared with us.";
        const depositNumber = deposit && parseInt(deposit);

        try {
            if (typeof depositNumber !== "number" || !defaultTerm) {
                onSubmitFailure(undefined, genericError);
                return;
            }

            const params = {
                preferredDepositAmount: depositNumber,
                selectedFinanceType: isHpShowing ? "HP" : "PCP",
                selectedTerm: defaultTerm,
                vehicleId: legacyVehicleId ?? stockBookNumber,
                ...(!isHpShowing && { mileage }),
            };

            const submitEvent = generateGtmFinanceSubmitEvent(params);

            const response = await getFinanceData(params, submitEvent);

            if (!Array.isArray(response)) {
                onSubmitFailure(undefined, genericError);
                return;
            }

            onSubmitSuccess(depositNumber);
        } catch (error) {
            onSubmitFailure(undefined, genericError);
        }
    };

    if (hasValidDeposit && typeof defaultDeposit === "number" && defaultMonthly) {
        return (
            <div id="finance-deal-widget" className={cx("financeDealWidget")}>
                <div className={cx("iconRow")}>
                    <UIIcon icon="Calculator" />
                </div>
                <div>
                    <div className={cx("textRow", "textRowSmallMargin")}>
                        <strong className={cx("textRowMain")}>Finance deal example</strong>
                    </div>
                    <div className={cx("despositMonthlyRow")}>
                        <div className={cx("despositContainer")}>
                            <span className={cx("depositHeading")}>
                                {hasValuation ? "Your contribution" : "Deposit"}
                            </span>
                            <span
                                className={cx("price")}
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(defaultDeposit + partExchangeAmount),
                                }}
                            />
                        </div>
                        <div className={cx("monthlyContainer")}>
                            <span className={cx("depositHeading")}>Monthly payment</span>
                            <div className={cx("priceContainer")}>
                                <span
                                    className={cx("price")}
                                    dangerouslySetInnerHTML={{
                                        __html: formatCurrency(defaultMonthly),
                                    }}
                                />
                                <span className={cx("perMonth")}>
                                    {` / month ${isHpShowing ? "HP" : "PCP"}`}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={cx("ctaContainer")}>
                        <div className={cx("buttonContainer", "buttonContainerStart")}>
                            <Button
                                type="button"
                                theme="secondary"
                                className={cx("inputRowButton")}
                                onClick={handleSeeFinanceCalculatorClick}
                                ariaLabel=" See finance calculator"
                            >
                                See finance calculator
                            </Button>
                        </div>
                        <div className={cx("buttonContainer")}>
                            <Button
                                type="button"
                                theme="textLink"
                                onClick={handleChangeDepositClick}
                                ariaLabel="Change deposit"
                            >
                                Change deposit
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="finance-deal-widget" className={cx("financeDealWidget", "form")}>
            <div className={cx("iconRow")}>
                <UIIcon icon="Calculator" />
            </div>
            {!!text && !!strongText && (
                <div className={cx("textRow")}>
                    {!!strongText && <span className={cx("textRowMain")}>{strongText}</span>}
                    {!!text && text}
                </div>
            )}
            <form className={cx("inputRow")} onSubmit={handleSubmit(onSubmit)}>
                <TextInput
                    icon="Pound"
                    className={cx("addDepositInput")}
                    placeholder={textInputPlaceholder}
                    alignRight
                    {...register("deposit", {
                        required: {
                            message: "Please enter a deposit",
                            value: true,
                        },
                    })}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                        handleKeyDownEvent(event, DIGITS_REGEX)
                    }
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) =>
                        handlePasteEvent(event, DIGITS_REGEX)
                    }
                    value={watch("deposit")}
                    errorText={formState.errors.deposit?.message}
                    hasError={!!formState.errors.deposit?.message}
                    maxLength={Math.round(vehicleAmount).toString().length}
                    // thousandSeparator=","
                    inputMode="numeric"
                    type="text"
                    pattern="\d*"
                />
                {!!buttonText && (
                    <Button
                        className={cx("inputRowButton")}
                        type="submit"
                        theme="secondary"
                        ariaLabel={buttonText}
                    >
                        {buttonText}
                    </Button>
                )}
            </form>
        </div>
    );
};

export default FinanceDealWidget;
